<template>
  <b-card no-body class="w-100">
    <div slot="header">
      <strong>{{ this.title }}</strong>
      <div class="card-header-actions">
        <!-- moving average switch with days input -->
        <moving-average-switch
          v-if="movingAvg"
          v-model="movingAvgValue" />

        <!-- seasonal switch -->
        <custom-switch
          v-if="seasonal"
          label="Seasonal"
          v-model="seasonalValue" />

        <template v-if="exportTypes && csvAccess">
          <b-button v-for="(exportType, index) in exportTypes"
                    @click="$emit('export', exportType)"
                    :key="`type-${index}`"
                    :disabled="isEmpty || exportLoading"
                    variant="primary"
                    size="sm"
                    class="download-button">
            <template v-if="exportLoading">
              <i class="fa fa-refresh fa-spin"></i>
            </template>
            <template v-else>
              {{ exportType.toUpperCase() }}
            </template>
          </b-button>
        </template>
      </div>
    </div>

    <b-card-body class="card-content">
      <div v-if="loading" class="loading-spinner">
        <i class="fa fa-5x fa-cog fa-spin text-muted m-2"></i>
      </div>

      <div class="chart-container">
        <div v-if="periodicStatEnabled" class="current-value">
          Current value: {{ currentValue }}
        </div>

        <slot name="top-controls">
          <!-- place additional controls here, like custom mode switcher -->
        </slot>

        <b-form class="controls-form">
          <div v-if="graphTypeSelector" class="controls-form__section">
            <icons-select
              label=""
              :disabled="!graphTypeAvailable"
              :choices="graphType.choices"
              v-model="graphType.value"
              @input="handleGraphTypeChange" />
          </div>
        </b-form>

        <div @mouseleave="onMouseLeaveChart">
          <apexchart
            :key="chartCounter"
            class="dashboard-chart h-100"
            type="line"
            height="370"
            :options="chartOptions"
            :series="chartSeries" />
        </div>

        <div class="controls-row">
          <graph-periodic-stat
            v-if="periodicStatEnabled"
            :dates="dates"
            :values="values"
            :current-data-index="currentDataIndex"
            :values-format="valuesFormat" />

          <graph-min-max-y-selector
            class="ml-auto"
            :values="plainValues"
            @min-changed="(value) => {this.yMinValue = value}"
            @max-changed="(value) => {this.yMaxValue = value}" />
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { mapActions } from 'vuex';
import VueApexCharts from 'vue-apexcharts'
import moment from 'moment'
import debounce from 'lodash-es/debounce'

import GraphMinMaxYSelector from './GraphMinMaxYSelector'
import {
  createApexTimeseries,
  createSeasonalApexTimeseries,
  timeseriesToMovingAvg,
  datesValuesToMovingAvg,
  seasonalDateFormatter,
} from '@/shared/apex-graphs-utils'
import GraphPeriodicStat from './GraphPeriodicStat'
import { mapGetters } from 'vuex'
import MovingAverageSwitch from './MovingAverageSwitch'
import CustomSwitch from './CustomSwitch'
import {
  formatFloat,
  formatPercent,
  formatVolume,
} from '@/shared/number-utils'
import GraphThemeMixin from '@/mixins/GraphThemeMixin';
import isEmpty from 'lodash-es/isEmpty';
import { seasonalPalette, stackedPalette } from '@/shared/constants/colors';
import IconsSelect from '@/components/widgets/IconsSelect';


export default {
  name: 'timeseries-chart',

  components: {
    IconsSelect,
    CustomSwitch,
    apexchart: VueApexCharts,
    MovingAverageSwitch,
    GraphPeriodicStat,
    GraphMinMaxYSelector,
  },

  mixins: [
    GraphThemeMixin,
  ],

  props: {
    loading: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: () => '',
    },

    series: {
      // used for when "grouped" is specified
      type: Array,
      default: () => [],
    },

    dates: {
      type: Array,
      default: () => [],
    },

    values: {
      type: Array,
      default: () => [],
    },

    valuesFormat: {
      // unset, percent, float1, float2, etc
      type: String,
      default: () => 'unset',
    },

    graphTypeSelector: {
      type: Boolean,
      default: false,
    },

    graphDefaultType: {
      type: String,
      default: 'line',
    },

    grouped: {
      type: Boolean,
      default: false
    },

    stacked: {
      type: Boolean,
      default: false
    },

    seasonal: {
      type: Boolean,
      default: true,
    },

    seasonalInitialValue: {
      type: Boolean,
      default: false,
    },

    movingAvg: {
      type: Boolean,
      default: true,
    },

    movingAvgInitialValue: {
      type: Number,
      default: null,
    },

    xAxis: {
      type: Object,
      default: () => ({
        titleText: undefined,
      }),
    },

    yAxis: {
      type: Object,
      default: () => ({
        titleText: '# vessels',
      }),
    },

    exportTypes: {
      type: Array,
      default: () => null,
    },

    exportLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const graphTypeChoices = [
      {text: 'Bar', value: 'bar', iconClass: 'fa fa-lg fa-bar-chart'},
      {text: 'Line', value: 'line', iconClass: 'fa fa-lg fa-line-chart'},
      {text: 'Area', value: 'area', iconClass: 'fa fa-lg fa-area-chart'}
    ];

    return {
      chartCounter: 0,
      seasonalValue: this.seasonalInitialValue,
      movingAvgValue: this.movingAvgInitialValue,  // days count, null is a disabled state
      graphType: {
        value: this.graphDefaultType,
        choices: graphTypeChoices,
      },
      yMaxValue: null,
      yMinValue: null,
      hoverDataIndex: null,
      user: {},
      csvAccess: false,

      labelTxt: {
        labelOn: 'on',
        labelOff: 'off'
      },
    };
  },

  async mounted() {
      await this.getUserData();
  },

  methods: {
    ...mapActions('api', ['getUser']),

    async getUserData() {
      this.user = await this.getUser();
      this.csvAccess = this.user['https://app.oceanbolt.com/user_authorization'].permissions.includes("download:csv") || false
    },

    handleGraphTypeChange() {
      // hack: chart.$forceUpdate and chart.updateOptions don't work here for some reason,
      // so it's needed to force rerender chart component with :key="chartCounter"
      // (the only way found so far)
      this.chartCounter += 1;
    },

    getFormattedValue(value) {
      if(typeof value !== 'number' && !value) {
        return '--';
      }

      if(value >= 1000) {
        return formatVolume(value, '100k');
      }

      if(this.valuesFormat === 'percent') {
        return formatPercent(value);
      }

      if(this.valuesFormat.startsWith('float')) {
        const regexp = this.valuesFormat.match(/float(\d+)/);
        const repeatCount = regexp ? regexp[1] : 1;
        return formatFloat(value, '0.' + '0'.repeat(repeatCount));
      }

      return Number.isInteger(value) ? value : value.toFixed(1);
    },

    createApexTimeseries(dates, values) {
      return dates.map(function(item, i) {
        return [moment(item, 'YYYY-MM-DD').valueOf(), values[i]];
      });
    },

    onChartHover: debounce(
      function(dataIndex) {
        this.hoverDataIndex = dataIndex;
      },
      100
    ),

    onMouseLeaveChart () {
      // to default data index (last item)
      // timeout is required when a user moves mouse too fast
      // "hoverDataIndex" must be set to null in the last order, a delay force this behaviour
      setTimeout(() => { this.hoverDataIndex = null }, 100);
    },
  },

  computed: {
    ...mapGetters( 'api', [
        'getActiveRequestsCount',
      ]
    ),

    periodicStatEnabled() {
      return !this.seasonalValue && !this.grouped;
    },

    graphTypeAvailable() {
      return !this.seasonalValue && !this.getActiveRequestsCount;
    },

    currentDataIndex() {
      if(this.hoverDataIndex) {
        return this.hoverDataIndex;
      } else {
        if(this.grouped) {
          return null;
        } else {
          return this.values.length - 1;
        }
      }
    },

    plainValues() {
      if(this.values) {
        return this.values;
      } else if(this.series) {
        let values = [];
        this.series.forEach(item => {
          values = values.concat(item.data.map(value => value[1]))
        });
        return values;
      } else {
        return [];
      }
    },

    currentValue() {
      if(this.grouped) {
        return null;
      } else {
        return this.getFormattedValue(
          this.values[this.currentDataIndex]
        );
      }
    },

    chartSeries() {
      let commonName = this.yAxis.titleText;
      let series = null;

      if(this.grouped) {
        if(this.movingAvgValue) {
          const movingAvgTimeseries = timeseriesToMovingAvg(this.series, this.movingAvgValue);
          return movingAvgTimeseries.map(item => ({
            ...item,
            type: this.graphType.value,
          }));
        } else {
          return this.series.map(item => ({
            ...item,
            type: this.graphType.value,
          }));
        }
      }

      if(this.seasonalValue) {
        if(this.movingAvgValue) {
          let [dates, values] = datesValuesToMovingAvg(this.dates, this.values, this.movingAvgValue);
          return createSeasonalApexTimeseries(dates, values, commonName, '(moving avg)');
        } else {
          return createSeasonalApexTimeseries(this.dates, this.values, commonName);
        }
      } else {
        series = createApexTimeseries(this.dates, this.values, commonName);
        if(this.movingAvgValue) {
          return timeseriesToMovingAvg(series, this.movingAvgValue);
        } else {
          return series;
        }
      }
    },

    chartOptions() {
      return {
        chart: {
          id: `graph${moment().format('YYYYMMDDHHmm')}`,
          background: this.themeBackground,
          type: this.graphType.value,
          stacked: this.stacked && ['bar', 'area'].includes(this.graphType.value),
        },

        colors: this.chartPalette,

        tooltip: {
          enabled: true,
          y: {
            formatter: (value, data) => {
              // a hack to get current date and value (debouncer is used in onChartHover)
              this.onChartHover(data.dataPointIndex);
              return this.getFormattedValue(value);
            },
          },
        },

        stroke: {
          show: true,
          colors: this.chartPalette,
          width: 3
        },

        theme: {
          // tooltip styling doesn't work correctly for now,
          // it's required to set the whole theme here

          mode: this.darkMode ? 'dark' : 'light',
        },

        legend: {
          show: true,
          formatter: function(seriesName) {
            let yearRegexp = seriesName.match(/\d{4}/);
            if(yearRegexp && seriesName.includes('moving')) {
              return `${yearRegexp[0]} (avg)`;
            } else if(yearRegexp) {
              return yearRegexp[0];
            } else {
              return seriesName;
            }
          }
        },

        xaxis: {
          type: 'datetime',
          labels: {
            formatter: this.seasonalValue ? seasonalDateFormatter.bind(this): undefined,
          },
          title: {
            text: this.xAxis.titleText,
          },
        },

        yaxis: {
          labels: {
            formatter: this.getFormattedValue.bind(this),
            style: {
              fontSize: '12px',
              fontFamily: 'Helvetica, Arial, sans-serif',
              cssClass: 'apexcharts-yaxis-label'
            }
          },
          title: {
            text: this.yAxis.titleText,
            style: {
              fontSize: '14px',
              fontFamily: 'Helvetica, Arial, sans-serif',
              cssClass: 'apexcharts-yaxis-label'
            }
          },
          min: typeof this.yMinValue === 'number' ? this.yMinValue : undefined,
          max: typeof this.yMaxValue === 'number' ? this.yMaxValue : undefined,
        },

        plotOptions: {
          line: {
            curve: 'smooth'
          }
        },

        dataLabels: {
          enabled: false
        },

        markers: {
          size: 0,
          style: 'full'
        }
      };
    },

    chartPalette() {
      if(this.seasonalValue) {
        return ['#2E93fA', ...seasonalPalette];
      }

      if(this.grouped) {
        return [...stackedPalette];
      } else {
        return ['#2E93fA'];
      }
    },

    isEmpty() {
      return isEmpty(this.dates) && isEmpty(this.values) && isEmpty(this.series);
    }
  },
};
</script>

<style scoped lang="scss">
.card-content {
  position: relative;
}

.chart-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.dashboard-chart {
  max-height: 300px;
}

.card-header-actions {
  display: flex;
}

.current-value {
  position: absolute;
  top: 10px;
  left: 20px;
  font-size: 15px;
  color: darkgray;
}

.controls-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
}

.download-button,
.download-button:focus,
.download-button:active {
  min-width: 40px;
  margin-left: 8px;
  padding: 1px 6px;
  outline: none !important;
  box-shadow: none !important;
}

.controls-form {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 12px 8px 0 0;

  &__section {
    display: flex;
  }
}
</style>
